import { PROPERTIES } from './group-scope.seed';
import { SERVICE_TICKET_ASSIGNEES } from './service-tickets.seed';
import type { AccountUser, AccountUsersStatus } from '@common/types';

const FIRST_NAMES = [
  'Alexander', 'Alexa', 'Andy', 'Ashish', 'Alex', 'Brent', 'Cendyn', 'Chris', 'Colleen', 'CS',
  'Demo', 'Deona', 'Paul', 'Dima', 'Eugene', 'g', 'Hotel', 'Deon', 'Habib', 'Hagen',
  'Hop', 'Test', 'Info', 'Inne', 'CCRM', 'Infor', 'James', 'Jamey', 'Joris', 'John',
  'Jack', 'Kane', '', 'Krystal', 'Shark', 'Marc', 'Megan', 'Michael', 'Michelle', 'Mike',
  'Dr', 'Niels', 'pm', 'Phil', 'pm', 'pm', 'pm', 'pm test', 'Dagan', 'Rekhaunsh'
];

const LAST_NAMES = [
  'Kalatsky', 'Scheepers', 'Heathershaw', 'Shetty', 'Webber', 'Harris', 'Demo', 'Alexandre', 'Bagley', 'Goh',
  'User', 'Mould', 'Moore', 'Nikitin', 'Sutula', 'b', 'Partner', 'Mostert', 'Portcities (ODOO)', 'The Portfolio Collection',
  'Software', 'Test', 'Info', '-', 'Integration', 'HMS', 'Roberts', 'Ries', 'Geerdes', 'Oburrah',
  'Pawson', 'Whitlie', '', 'Ramirez', 'Laser', 'Dorney', 'Erasmus', 'UserPilot', 'Test', 'Mayo',
  'Evil', 'Mekenkamp', 'testing', 'Broderick', 'ttest', 'test', 'testing', 'Gareth@guestrevu.com', 'Read', 'Gadge'
];

const EMAILS = [
  'akalatsky@hapicloud.io', 'alexascheepers@gmail.com', 'andy.heathershaw@premiersoftware.co.uk', 'ashish.shetty@waterways-leisure.com', 'awebber@hubspot.com',
  'bharris@cendyn.com', 'cendyn@guestrevu.com', 'chris@alexandre.cx', 'colleen@guestrevu.com', 'cs.goh@roomie.my',
  'demouser@thelandon.com', 'deonamould96@gmail.com', 'developers@guestrevu.com', 'dnikitin@hapicloud.io', 'eugene@webhive.com.ua',
  'garethb@sharklasers.com', 'guestrevu@hotelpartner.com.tr', 'guestxdeon@bypetousis.co.za', 'habib@portcities.net', 'hagen@limabean.co.za',
  'help@hopsoftware.com', 'info@auroraandgrace.com', 'info@guestrevu.com', 'inne@digitalsolusindo.id', 'integrations@cendyn.com',
  'it@obi.mu', 'james.roberts@brookson.co.uk', 'jameyblindsideries@gmail.com', 'jgeerdes@michelreybier.com', 'john@techno-savvy.net',
  'jp@lasers.com', 'kanewhitlie@gmail.com', 'kelly@guestrevu.com', 'kramirez@cendyn.com', 'ksp602+4i3oqr5tqp754@sharklasers.com',
  'marc@hartpms.com', 'megnb@guestrevu.com', 'michael@userpilot.com', 'michelle@test.com', 'mike@intellimetrixx.com',
  'minnieme@sharklasers.com', 'nmekenkamp@cendyn.com', 'nomad77744@gmail.com', 'phil.broderick@for-sight.co.uk', 'pm333333@sharklasers.com',
  'pmtest2@sharklasers.com', 'pmtesting@sharklasers.com', 'pmuserinvite@sharklasers.com', 'read.dagan@gmail.com', 'rekhaunsh.gadge@ideas.com'
];

const JOB_TITLES = [
  'Salesforce Architect', '-', 'CTO at Premiere Software', 'Test user', 'HubSpot support', 'Cendyn', 'Cendyn', '', 'Sales', 'Roomie User',
  '', 'Test', 'Brand Manager', 'Salesforce Architect / Technical Manager', 'RoomSpace Developer', 'pm', '-', 'Vineyard', 'API user', 'Limabean Director',
  'Integration', 'Test', '', '-', 'CCRM API Integration', 'Test User', 'Digital Innovation & Strategy Manager', '', 'Data Analyst', 'HotelMaster (TechnoSavvy Integration)',
  'CSMO', 'Graphic Designer', '', 'Cendyn', 'Shark Laser Test', '-', '', 'Dev User', 'test', '',
  'Supervillian', 'Cendyn', 'pm ( Nomad Gmail)', 'Product Growth Development', 'test', 'test', 'pm', 'tester', 'Programmer', 'Ideas Development'
];

const STATUS = [
  'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'DELETED', 'ACTIVE',
  'ACTIVE', 'PENDING', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'PENDING', 'ACTIVE', 'DELETED', 'ACTIVE', 'ACTIVE',
  'ACTIVE', 'PENDING', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'ACTIVE', 'PENDING', 'ACTIVE', 'ACTIVE', 'ACTIVE',
  'ACTIVE', 'ACTIVE', 'DELETED', 'ACTIVE', 'PENDING', 'ACTIVE', 'DELETED', 'ACTIVE', 'PENDING', 'DELETED',
  'DELETED', 'DELETED', 'DELETED', 'ACTIVE', 'PENDING', 'DELETED', 'PENDING', 'DELETED', 'DELETED', 'ACTIVE'
];

const STATUSES: AccountUsersStatus[] = ['active', 'new', 'pending', 'deleted'];

function pick<T>(arr: T[], i: number): T {
  return arr[i % arr.length];
}

function buildSeedUsers(count: number): AccountUser[] {
  const users: AccountUser[] = [];
  for (let i = 0; i < count; i++) {
    const first = pick(FIRST_NAMES, i);
    const last = pick(LAST_NAMES, (i * 7) % LAST_NAMES.length);
    const title = pick(JOB_TITLES, i);
    const status = STATUSES.find((s) => s.toUpperCase() === pick(STATUS, i)) ?? 'active';

    const email = pick(EMAILS, i);
    users.push({
      id: (i + 1).toString(),
      accountIds: PROPERTIES.map(p => p.id),
      name: first,
      surname: last,
      email,
      title,
      status,
    });
  }
  return users;
}

export const ACCOUNT_USERS_SEED: readonly AccountUser[] = [
  ...buildSeedUsers(50),
  ...SERVICE_TICKET_ASSIGNEES.map(user => ({
    id: user.id, name: user.name.split(' ')[0], surname: user.name.split(' ').slice(1).join(' '),
    title: '', email: '', status: 'active' as const, accountIds: PROPERTIES.map(p => p.id),
  })),
];
